import React from 'react'
import { useAppStore } from '../store/useAppStore'
import { OLYMPIC_AGENCIES } from '../utils/agencies'
import { Car, User, Calendar, Hash, ArrowRight, ChevronLeft } from 'lucide-react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import { PHOTO_SLOTS } from '../utils/constants'
import { buildAuthHeader } from '../utils/auth'
import { BACKEND } from '../utils/config'


function fmtDate(d: string | Date | null | undefined) {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch { return '—' }
}

export default function RecapScreen() {
  // ✅ Sélecteurs atomiques — évite les re-renders inutiles
  const inspection  = useAppStore(s => s.inspection)
  const setStep     = useAppStore(s => s.setStep)
  const credentials = useAppStore(s => s.credentials)

  const { contract, type } = inspection
  const ag = OLYMPIC_AGENCIES.find(a => String(a.id) === String(contract?.agenceDepartId))

  const [departData, setDepartData] = React.useState<{ mileage?: number, fuel?: number, damages?: any[] } | null>(null)

  // ✅ Date d'ouverture de l'écran mémorisée — ne change pas entre les renders
  const now = React.useMemo(() => new Date().toISOString(), [])

  // ✅ useEffect avec dépendances complètes
  React.useEffect(() => {
    if (type !== 'retour') return
    const auth = buildAuthHeader(credentials)
    fetch(`${BACKEND}/api/edl/${contract?.rawId || contract?.id}`, {
      headers: { 'X-MRC-Auth': auth }
    })
      .then(r => r.json())
      .then(data => { if (data.depart) setDepartData(data.depart) })
      .catch(() => {})
  }, [type, contract?.rawId, contract?.id, credentials])

  // ✅ Harmonisation conductorName / driverName
  const driverName = contract?.driverName || contract?.conductorName

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">

      {/* Header compact */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep('contracts')}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-semibold mr-1"
          >
            <ChevronLeft size={18}/> Contrats
          </button>
          <div
            className="w-1 h-8 rounded-full flex-shrink-0"
            style={{ backgroundColor: ag?.color || '#e5e7eb' }}
          />
          <div>
            <h1 className="font-black text-gray-900 text-base">Récapitulatif du contrat</h1>
            <p className="text-xs text-gray-400">{contract?.plate} · {contract?.vehicleName}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
          type === 'depart' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
        }`}>
          {type === 'depart' ? 'DÉPART' : 'RETOUR'}
        </span>
      </div>

      {/* Corps : portrait = colonne, paysage = ligne */}
      <div className="flex-1 overflow-auto p-4 flex flex-col landscape:flex-row landscape:items-start gap-4">

        {/* Colonne principale */}
        <div className="flex-1 flex flex-col gap-3 min-w-0">

          {/* Véhicule */}
          <Card className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Car size={18} className="text-blue-600"/>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Véhicule</p>
              <p className="font-black text-gray-900 truncate">{contract?.vehicleName || '—'}</p>
              <p className="text-xs text-gray-500">
                {contract?.plate}
                {contract?.category && (
                  <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                    {contract.category}
                  </span>
                )}
              </p>
            </div>
          </Card>

          {/* Client */}
          <Card className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <User size={18} className="text-purple-600"/>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Client / Conducteur</p>
              <p className="font-black text-gray-900 truncate">{contract?.clientName || '—'}</p>
              {/* ✅ driverName harmonisé (driverName || conductorName) */}
              {driverName && driverName !== contract?.clientName && (
                <p className="text-xs text-gray-500">Conducteur : {driverName}</p>
              )}
            </div>
          </Card>

          {/* Dates */}
          <Card className="p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Calendar size={18} className="text-green-600"/>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Dates</p>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-green-700 w-24 flex-shrink-0 mt-0.5">Départ :</span>
                  <span className="text-sm font-bold text-gray-800">{fmtDate(contract?.startDate)}</span>
                </div>
                {type === 'retour' ? (
                  <>
                    {/* ✅ now mémorisé — stable entre les renders */}
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold text-orange-600 w-24 flex-shrink-0 mt-0.5">Retour réel :</span>
                      <span className="text-sm font-bold text-orange-800">{fmtDate(now)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-gray-400 w-24 flex-shrink-0 mt-0.5">Prévu :</span>
                      <span className="text-sm text-gray-500">{fmtDate(contract?.endDate)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-orange-600 w-24 flex-shrink-0 mt-0.5">Retour prévu :</span>
                    <span className="text-sm font-bold text-gray-800">{fmtDate(contract?.endDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* N° contrat */}
          <Card className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Hash size={18} className="text-orange-600"/>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">N° Contrat</p>
              <p className="font-black text-gray-900 text-xl">{contract?.numeroDocument || contract?.id}</p>
            </div>
          </Card>

          {/* Données départ (retour uniquement) */}
          {type === 'retour' && departData && (
            <Card className="p-4 bg-blue-50 border border-blue-200">
              <p className="text-[10px] uppercase tracking-wider text-blue-600 font-bold mb-2">État au départ</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-black text-blue-700">{departData.mileage || '—'}</p>
                  <p className="text-[10px] text-blue-500">km départ</p>
                </div>
                <div>
                  <p className="text-lg font-black text-blue-700">
                    {departData.fuel != null ? `${departData.fuel}/8` : '—'}
                  </p>
                  <p className="text-[10px] text-blue-500">carburant</p>
                </div>
                <div>
                  <p className="text-lg font-black text-blue-700">{departData.damages?.length || 0}</p>
                  <p className="text-[10px] text-blue-500">dommages</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* CTA */}
        <div className="flex-shrink-0 landscape:w-60 flex flex-col gap-3">
          <Card className="p-4 bg-blue-50 border border-blue-200">
            <p className="text-xs text-blue-700 font-medium text-center leading-relaxed">
              Vous allez effectuer <strong>{PHOTO_SLOTS.length} photos guidées</strong> du véhicule
              avec annotation des dommages éventuels.
            </p>
          </Card>
          <Button onClick={() => setStep('photos')} size="xl" className="w-full">
            Commencer l'état des lieux <ArrowRight size={20}/>
          </Button>
        </div>

      </div>
    </div>
  )
}